import { Suspense, lazy, useEffect, useLayoutEffect, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { activeLeagueManagementMembership, canShowAITab, initials, preferredName } from "@/models/user";
import { selectedWorkspaceOption } from "@/auth/workspaceOptions";
import { AIChatProvider } from "@/features/ai/AIChatProvider";
import { SBotBrandLogo } from "@/components/SBotBrandLogo";
import { useNotificationCenter } from "@/features/notifications/NotificationCenterProvider";
import { usePushNotifications } from "@/features/notifications/PushNotificationProvider";
import "./shell.css";

// Mounted outside <Outlet/> so the floating presentation can replace /ai
// without interrupting the shared controller's in-flight conversation.
const FloatingAIChat = lazy(() =>
  import("@/features/ai/widget/FloatingAIChat").then((module) => ({ default: module.FloatingAIChat })),
);
// External-agent (WebMCP) bridge: registers the AI tools on document.modelContext
// for the whole shell, including /ai where the floating chat is hidden.
const WebMCPBridgeHost = lazy(() =>
  import("@/features/ai/webmcp/WebMCPBridgeHost").then((module) => ({ default: module.WebMCPBridgeHost })),
);

// Web equivalent of RootView's MainTab bar. The AI tab mirrors iOS gating
// (hidden for staff-only roles unless SBot Labs admin). League tab appears
// for league admins/managers or SBot Labs admins; when admin mode is on it
// becomes the Admin tab, mirroring MainTab.title().

function WorkspaceSwitcher() {
  const { currentUser, workspaceOptions, switchActiveWorkspace, isLoading, isWorkspaceUntethered } = useAuth();
  if (!currentUser || workspaceOptions.length === 0) return null;

  const selected = selectedWorkspaceOption(currentUser, workspaceOptions);
  if (workspaceOptions.length === 1 && selected) {
    return (
      <div className="shell-workspace">
        <span className="shell-workspace-label">Workspace</span>
        <span className="shell-workspace-single" title={selected.displayTitle}>{selected.displayTitle}</span>
      </div>
    );
  }

  return (
    <div className="shell-workspace">
      <label className="shell-workspace-label" htmlFor="shell-workspace">Workspace</label>
      <select
        id="shell-workspace"
        className="shell-workspace-select"
        value={selected?.membershipID ?? ""}
        disabled={isLoading}
        onChange={(event) => void switchActiveWorkspace(event.target.value)}
        aria-label="Active workspace"
        title={isWorkspaceUntethered ? "Untethered — changes apply to this window only" : undefined}
      >
        {workspaceOptions.map((option) => (
          <option key={option.membershipID} value={option.membershipID}>
            {option.displayTitle}
          </option>
        ))}
      </select>
    </div>
  );
}

export function AppShell() {
  const {
    currentUser,
    isSBotLabsAdmin,
    isSBotAdminModeEnabled,
    setSBotAdminModeEnabled,
  } = useAuth();
  const { badgeText } = useNotificationCenter();
  const {
    foregroundMessage,
    dismissForegroundMessage,
  } = usePushNotifications();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const shellRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);


  useEffect(() => {
    if (
      isAdminPresentationRoute(pathname)
      && isSBotLabsAdmin
      && !isSBotAdminModeEnabled
    ) {
      setSBotAdminModeEnabled(true);
    }
  }, [
    isSBotAdminModeEnabled,
    isSBotLabsAdmin,
    pathname,
    setSBotAdminModeEnabled,
  ]);

  const showLeagueTab = isSBotLabsAdmin || (currentUser ? activeLeagueManagementMembership(currentUser) !== undefined : false);
  const showAITab = canShowAITab(currentUser, isSBotLabsAdmin);
  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const updateHeight = () => {
      shellRef.current?.style.setProperty("--shell-header-height", `${header.getBoundingClientRect().height}px`);
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, [showAITab]);

  const isMapLayoutRoute =
    /^\/competitions\/[^/]+\/gps\/map$/.test(pathname) ||
    /^\/competitions\/[^/]+\/courses$/.test(pathname) ||
    pathname === "/league/course-encoder" ||
    pathname === "/ai";
  const isOverlayDesignerRoute = /^\/competitions\/[^/]+\/overlays\/[^/]+$/.test(pathname);
  const shellClassName = isMapLayoutRoute
    ? "shell shell-map-layout"
    : isOverlayDesignerRoute
      ? "shell shell-designer-layout"
      : "shell";

  const shell = (
    <div className={shellClassName} ref={shellRef}>
      <header className="shell-header" ref={headerRef}>
        <NavLink to="/home" className="shell-brand" aria-label="SBot Labs home">
          <SBotBrandLogo className="shell-brand-mark" />
          <span className="shell-brand-name">SBot Labs</span>
        </NavLink>

        <div className="shell-header-right">
          <WorkspaceSwitcher />
          {currentUser && (
            <NavLink to="/account" className="shell-avatar" aria-label={`Account for ${preferredName(currentUser)}`} title={preferredName(currentUser)}>
              {currentUser.photoURL ? <img src={currentUser.photoURL} alt="" /> : initials(currentUser)}
            </NavLink>
          )}
        </div>

        <nav className="shell-tabs" aria-label="Main">
          <NavLink to="/home" className={({ isActive }) => (isActive ? "active" : "")}>
            Home
          </NavLink>
          {showAITab && (
            <NavLink to="/ai" className={({ isActive }) => (isActive ? "active" : "")}>
              AI
            </NavLink>
          )}
          {showLeagueTab && (
            <NavLink
              to={isSBotAdminModeEnabled ? "/admin" : "/league"}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              {isSBotAdminModeEnabled ? "Admin" : "League"}
            </NavLink>
          )}
          <NavLink to="/account" className={({ isActive }) => (isActive ? "active" : "")}>
            Account
            {badgeText && <span className="shell-tab-badge" aria-label={`${badgeText} unread notifications`}>{badgeText}</span>}
          </NavLink>
        </nav>


      </header>

      <main className="shell-content">
        <Outlet />
      </main>

      {foregroundMessage && (
        <div className="shell-push-banner" role="status" aria-live="polite">
          <div className="shell-push-copy">
            <strong>{foregroundMessage.title}</strong>
            {foregroundMessage.body && <span>{foregroundMessage.body}</span>}
          </div>
          <button
            type="button"
            className="shell-push-open"
            onClick={() => {
              const path = foregroundMessage.notificationID
                ? `/account/notifications/${encodeURIComponent(foregroundMessage.notificationID)}`
                : "/account/notifications";
              dismissForegroundMessage();
              navigate(path);
            }}
          >
            Open
          </button>
          <button
            type="button"
            className="shell-push-dismiss"
            aria-label="Dismiss notification"
            onClick={dismissForegroundMessage}
          >
            ×
          </button>
        </div>
      )}

      {showAITab && pathname !== "/ai" && (
        <Suspense fallback={null}>
          <FloatingAIChat />
        </Suspense>
      )}
      {showAITab && (
        <Suspense fallback={null}>
          <WebMCPBridgeHost />
        </Suspense>
      )}
    </div>
  );

  return showAITab ? <AIChatProvider>{shell}</AIChatProvider> : shell;
}

export function isAdminPresentationRoute(pathname: string): boolean {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}
